import React from 'react';
import { Box, Paper, Badge, Typography } from '@mui/material';
import {
  HomeOutlined,
  Home,
  DashboardOutlined,
  Dashboard,
  CalendarTodayOutlined,
  CalendarToday,
  NotificationsOutlined,
  Notifications,
  PersonOutline,
  Person,
  SearchOutlined,
  Search,
  WorkOutline,
  Work,
  LoginOutlined,
  HandymanOutlined,
  Handyman,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import TranslatedText from '../Common/TranslatedText';

const TAB_COLOR_ACTIVE = '#8B4513';
const TAB_COLOR_INACTIVE = '#6B5648';

const NavTab = ({ value, label, activeTab, icon: InactiveIcon, iconActive: ActiveIcon, badge, onClick }) => {
  const isActive = activeTab === value;
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        flex: 1,
        // Allow the flex item to shrink below its content width so 5 tabs
        // always fit, even when the OS enlarges the system font/display size.
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        py: 1,
        px: 0.5,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        outline: 'none',
        WebkitTapHighlightColor: 'transparent',
        position: 'relative',
        transition: 'transform 0.15s ease',
        '&:active': { transform: 'scale(0.9)' },
      }}
    >
      {/* Active indicator dot */}
      {isActive && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: 'rgba(139, 69, 19, 0.1)',
          }}
        />
      )}

      {/* Icon with optional badge */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {badge > 0 ? (
          <Badge
            badgeContent={badge}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#E96A6A',
                color: '#fff',
                fontSize: '0.55rem',
                minWidth: 15,
                height: 15,
                top: 1,
                right: 1,
                fontWeight: 700,
              },
            }}
          >
            {isActive
              ? <ActiveIcon sx={{ fontSize: 22, color: TAB_COLOR_ACTIVE }} />
              : <InactiveIcon sx={{ fontSize: 22, color: TAB_COLOR_INACTIVE }} />}
          </Badge>
        ) : (
          isActive
            ? <ActiveIcon sx={{ fontSize: 22, color: TAB_COLOR_ACTIVE }} />
            : <InactiveIcon sx={{ fontSize: 22, color: TAB_COLOR_INACTIVE }} />
        )}
      </Box>

      <Typography
        component="span"
        sx={{
          // Fluid label size: shrinks on very narrow screens and never
          // grows enough to wrap, keeping all tabs aligned regardless of
          // device width or OS font-scaling.
          fontSize: 'clamp(0.5rem, 2.6vw, 0.62rem)',
          fontWeight: isActive ? 700 : 600,
          color: isActive ? TAB_COLOR_ACTIVE : TAB_COLOR_INACTIVE,
          lineHeight: 1,
          letterSpacing: 0.1,
          zIndex: 1,
          // Stay on a single line and truncate gracefully instead of
          // wrapping and pushing the bar taller / misaligning icons.
          maxWidth: '100%',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const BottomNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, hasToken, isWorkerProfileComplete, hasActiveServices } = useAuth();
  const { unreadCount } = useSocket();

  const isAuth = isAuthenticated || hasToken;

  const getActiveTab = () => {
    const p = location.pathname;
    if (p === '/') return 'home';
    if (p.startsWith('/dashboard')) return 'dashboard';
    if (p.startsWith('/bookings') || p.startsWith('/book-worker')) return 'bookings';
    if (p.startsWith('/notifications')) return 'notifications';
    if (p.startsWith('/worker/profile')) return 'myservices';
    if (p.startsWith('/profile') || p.startsWith('/complete-profile') || p.startsWith('/complete-worker-profile')) return 'profile';
    if (p.startsWith('/search') || p.startsWith('/workers')) return 'search';
    if (p.startsWith('/job-requests') || p.startsWith('/my-job-requests') || p.startsWith('/post-work-request')) return 'jobs';
    if (p.startsWith('/login') || p.startsWith('/register')) return 'signin';
    return 'home';
  };

  const activeTab = getActiveTab();

  // Hide the bottom tab bar on the Booking Details screen (/bookings/:id) so it
  // can present its own always-visible sticky action bar instead. The Bookings
  // list (/bookings) and other routes are unaffected.
  const isBookingDetailsPage = /^\/bookings\/[^/]+$/.test(location.pathname);
  if (isBookingDetailsPage) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        display: { xs: 'block', md: 'none' },
        borderTop: '1px solid #EDE6E0',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        borderRadius: '16px 16px 0 0',
        overflow: 'hidden',
        paddingBottom: 'env(safe-area-inset-bottom)',
        backgroundColor: '#FFFFFF',
        // Own compositing layer: avoids iOS Safari repaint gaps on this fixed bar.
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', height: 62 }}>
        {isAuth ? (
          <>
            <NavTab
              value="home"
              label="Home"
              activeTab={activeTab}
              icon={HomeOutlined}
              iconActive={Home}
              onClick={() => navigate('/')}
            />
            <NavTab
              value="dashboard"
              label="Dashboard"
              activeTab={activeTab}
              icon={DashboardOutlined}
              iconActive={Dashboard}
              onClick={() => navigate('/dashboard')}
            />
            <NavTab
              value="bookings"
              label={<TranslatedText>Bookings</TranslatedText>}
              activeTab={activeTab}
              icon={CalendarTodayOutlined}
              iconActive={CalendarToday}
              onClick={() => navigate('/bookings')}
            />
            {isWorkerProfileComplete && hasActiveServices ? (
              <NavTab
                value="myservices"
                label="My Services"
                activeTab={activeTab}
                icon={HandymanOutlined}
                iconActive={Handyman}
                onClick={() => navigate('/worker/profile')}
              />
            ) : (
              <NavTab
                value="notifications"
                label="Alerts"
                activeTab={activeTab}
                icon={NotificationsOutlined}
                iconActive={Notifications}
                badge={unreadCount}
                onClick={() => navigate('/notifications')}
              />
            )}
            <NavTab
              value="profile"
              label="Profile"
              activeTab={activeTab}
              icon={PersonOutline}
              iconActive={Person}
              onClick={() => navigate('/profile')}
            />
          </>
        ) : (
          <>
            <NavTab
              value="home"
              label="Home"
              activeTab={activeTab}
              icon={HomeOutlined}
              iconActive={Home}
              onClick={() => navigate('/')}
            />
            <NavTab
              value="search"
              label="Search"
              activeTab={activeTab}
              icon={SearchOutlined}
              iconActive={Search}
              onClick={() => navigate('/search')}
            />
            <NavTab
              value="jobs"
              label="Jobs"
              activeTab={activeTab}
              icon={WorkOutline}
              iconActive={Work}
              onClick={() => navigate('/job-requests')}
            />
            <NavTab
              value="signin"
              label="Sign In"
              activeTab={activeTab}
              icon={LoginOutlined}
              iconActive={LoginOutlined}
              onClick={() => navigate('/login')}
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default BottomNavBar;
